import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Lock, User, Loader2, Package, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const VendorPortalLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const session = localStorage.getItem('vendor_session');
    if (session) {
      navigate('/vendor-portal/dashboard');
    }

    // Update manifest link for vendor portal
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = '/vendor-manifest.json';
    }

    // PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Check if not already installed
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      // Restore original manifest
      if (manifestLink) {
        manifestLink.href = '/manifest.json';
      }
    };
  }, [navigate]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      toast.info('To install, use your browser menu and select "Add to Home Screen"');
      return;
    }
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App installed successfully!');
    }
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/enterprise/vendor-portal/login`, credentials);
      
      // Store vendor session
      localStorage.setItem('vendor_session', JSON.stringify({
        vendor_id: response.data.vendor_id,
        vendor_name: response.data.vendor_name,
        session_token: response.data.session_token
      }));
      
      toast.success(`Welcome, ${response.data.vendor_name}!`);
      navigate('/vendor-portal/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col items-center justify-center p-4 safe-area-inset">
      {/* Install Banner */}
      {showInstallBanner && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed top-0 left-0 right-0 bg-amber-500 text-black px-4 py-3 flex items-center justify-between z-50"
        >
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            <span className="text-sm font-medium">Install app for better experience</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowInstallBanner(false)}
              className="text-black hover:bg-amber-600"
            >
              Later
            </Button>
            <Button
              size="sm"
              onClick={handleInstallClick}
              className="bg-black text-amber-500 hover:bg-gray-900"
            >
              <Download className="h-4 w-4 mr-1" />
              Install
            </Button>
          </div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md px-4"
      >
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 mb-4">
            <Building2 className="h-7 w-7 sm:h-8 sm:w-8 text-amber-400" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">Vendor Portal</h1>
          <p className="text-sm sm:text-base text-gray-400">Login to view your Purchase Orders</p>
        </div>

        {/* Login Form */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-5 sm:p-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-gray-300 text-sm">Username</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  placeholder="Enter your username"
                  className="pl-10 h-12 bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-500 focus:border-amber-500 text-base"
                  autoComplete="username"
                  autoCapitalize="none"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-300 text-sm">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  placeholder="Enter your password"
                  className="pl-10 h-12 bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-500 focus:border-amber-500 text-base"
                  autoComplete="current-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold h-12 sm:h-14 text-base"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                <>
                  <Package className="h-4 w-4 mr-2" />
                  Login to Portal
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Install App Button (Mobile) */}
        <div className="mt-4 text-center">
          <Button
            variant="ghost"
            onClick={handleInstallClick}
            className="text-gray-400 hover:text-amber-400 text-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Install App
          </Button>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-xs sm:text-sm mt-4 sm:mt-6">
          Powered by{' '}
          <a
            href="https://opswarex.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 hover:text-amber-500 underline-offset-2 hover:underline transition-colors"
            data-testid="powered-by-opswarex-link"
          >
            Opswarex
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default VendorPortalLoginPage;
